import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Calendar, Trash2, Plus, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ddd_admin_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const DOW = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

function todayLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateLabel(yyyyMmDd) {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function ScheduleManager() {
  const [loading, setLoading] = useState(true);
  const [savingHours, setSavingHours] = useState(false);
  const [savingInterval, setSavingInterval] = useState(false);
  const [hours, setHours] = useState(null); // { mon:{start,end}, ... }
  const [overrides, setOverrides] = useState([]);
  const [interval, setIntervalMin] = useState(30);

  // Override form state
  const [ovrDate, setOvrDate] = useState(todayLocal());
  const [ovrBlackout, setOvrBlackout] = useState(true);
  const [ovrStart, setOvrStart] = useState("09:00");
  const [ovrEnd, setOvrEnd] = useState("20:00");
  const [ovrNote, setOvrNote] = useState("");
  const [savingOvr, setSavingOvr] = useState(false);

  // Season closure state
  const [closureActive, setClosureActive] = useState(false);
  const [closureReopen, setClosureReopen] = useState("2027-05-01");
  const [closureMessage, setClosureMessage] = useState("");
  const [savingClosure, setSavingClosure] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/schedule`, { headers: authHeaders() });
      setHours(res.data.weekday_hours);
      setOverrides(res.data.overrides || []);
      if (res.data.slot_interval_minutes) setIntervalMin(res.data.slot_interval_minutes);
      const sc = res.data.season_closure || {};
      setClosureActive(!!sc.active);
      if (sc.reopen_date) setClosureReopen(sc.reopen_date);
      if (sc.message !== undefined) setClosureMessage(sc.message || "");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.href = "/admin";
        return;
      }
      toast.error("Could not load schedule");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const updateHour = (key, field, value) => {
    setHours((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      await axios.put(
        `${API}/admin/schedule/weekday_hours`,
        { weekday_hours: hours },
        { headers: authHeaders() },
      );
      toast.success("Weekly hours saved");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSavingHours(false);
    }
  };

  const saveInterval = async () => {
    const v = parseInt(interval, 10);
    if (isNaN(v) || v < 5 || v > 180) {
      toast.error("Interval must be between 5 and 180 minutes");
      return;
    }
    setSavingInterval(true);
    try {
      await axios.put(
        `${API}/admin/schedule/slot_interval`,
        { slot_interval_minutes: v },
        { headers: authHeaders() },
      );
      toast.success(`Slot interval set to ${v} min`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSavingInterval(false);
    }
  };

  const addOverride = async (e) => {
    e.preventDefault();
    setSavingOvr(true);
    try {
      const payload = {
        date: ovrDate,
        blackout: ovrBlackout,
        start: ovrBlackout ? null : ovrStart,
        end: ovrBlackout ? null : ovrEnd,
        note: ovrNote.trim() || null,
      };
      await axios.put(`${API}/admin/schedule/override`, payload, {
        headers: authHeaders(),
      });
      toast.success(ovrBlackout ? "Blackout date added" : "Custom day saved");
      setOvrNote("");
      reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSavingOvr(false);
    }
  };

  const deleteOverride = async (date) => {
    if (!window.confirm(`Remove override for ${formatDateLabel(date)}?`)) return;
    try {
      await axios.delete(`${API}/admin/schedule/override/${date}`, {
        headers: authHeaders(),
      });
      toast.success("Removed");
      reload();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const saveClosure = async (nextActive) => {
    const active = typeof nextActive === "boolean" ? nextActive : closureActive;
    if (active) {
      if (!closureReopen) {
        toast.error("Set a reopen date first");
        return;
      }
      if (closureReopen <= todayLocal()) {
        toast.error("Reopen date must be in the future");
        return;
      }
    }
    setSavingClosure(true);
    try {
      const res = await axios.put(
        `${API}/admin/schedule/season-closure`,
        {
          active,
          reopen_date: active ? closureReopen : null,
          message: closureMessage,
        },
        { headers: authHeaders() },
      );
      setClosureActive(!!res.data?.season_closure?.active);
      toast.success(active ? "Season closure ON — calendar closed" : "Season closure OFF — booking open");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSavingClosure(false);
    }
  };

  if (loading || !hours) {
    return (
      <div className="flex items-center gap-2 text-[#0B132B]/80 my-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…
      </div>
    );
  }

  return (
    <section data-testid="admin-schedule" className="space-y-6">
      <div
        data-testid="admin-season-closure-card"
        className="comic-card-static p-5 sm:p-6"
        style={{
          background: closureActive ? "#FFF59D" : "#FFFFFF",
        }}
      >
        <div className="flex items-center justify-between gap-3 flex-nowrap">
          <div className="font-['Bebas_Neue'] text-2xl tracking-wide">
            Season Closure
          </div>
          <div
            data-testid="admin-season-closure-status"
            className="shrink-0 text-xs font-bold px-3 py-1 rounded-full border-[3px] border-[#0B132B]"
            style={{
              background: closureActive ? "#FF3D00" : "#00E676",
              color: closureActive ? "#FFFFFF" : "#0B132B",
              boxShadow: "3px 3px 0 #0B132B",
              letterSpacing: "0.08em",
            }}
          >
            {closureActive ? "CLOSED" : "OPEN"}
          </div>
        </div>
        <p className="text-sm text-[#0B132B]/70 mt-2 max-w-2xl">
          Turn this ON to close the public calendar until a reopen date. A
          sticky note will appear on the landing page telling customers when
          you're back. Customers can still book dates on or after the reopen
          date.
        </p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-1">
              Reopen date
            </label>
            <input
              type="date"
              value={closureReopen}
              min={todayLocal()}
              onChange={(e) => setClosureReopen(e.target.value)}
              className="comic-input w-full"
              data-testid="admin-season-closure-reopen-date"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Note to customers (optional)
            </label>
            <input
              type="text"
              value={closureMessage}
              onChange={(e) => setClosureMessage(e.target.value)}
              placeholder="e.g. Focusing on student-athlete season"
              className="comic-input w-full"
              data-testid="admin-season-closure-message"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          {closureActive ? (
            <>
              <button
                type="button"
                onClick={() => saveClosure(true)}
                disabled={savingClosure}
                className="comic-btn comic-btn-primary w-full sm:w-auto"
                data-testid="admin-season-closure-save"
                style={{ padding: "0.55rem 1rem" }}
              >
                {savingClosure ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}{" "}
                Save changes
              </button>
              <button
                type="button"
                onClick={() => saveClosure(false)}
                disabled={savingClosure}
                className="comic-btn comic-btn-action w-full sm:w-auto"
                data-testid="admin-season-closure-turn-off"
                style={{ padding: "0.55rem 1rem" }}
              >
                Reopen booking now
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => saveClosure(true)}
              disabled={savingClosure}
              className="comic-btn comic-btn-primary w-full sm:w-auto"
              data-testid="admin-season-closure-turn-on"
              style={{ padding: "0.55rem 1rem" }}
            >
              {savingClosure ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}{" "}
              Close season until reopen date
            </button>
          )}
        </div>
      </div>

      <div className="comic-card-static p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-['Bebas_Neue'] text-2xl tracking-wide">
              Slot Interval
            </div>
            <p className="text-sm text-[#0B132B]/70 mt-1">
              How many minutes between each bookable time slot.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={[30, 45, 60, 90].includes(interval) ? String(interval) : "custom"}
              onChange={(e) => {
                if (e.target.value === "custom") setIntervalMin(interval || 30);
                else setIntervalMin(parseInt(e.target.value, 10));
              }}
              className="comic-input"
              style={{ padding: "0.45rem 0.6rem" }}
              data-testid="schedule-interval-select"
            >
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">60 minutes</option>
              <option value="90">90 minutes</option>
              <option value="custom">Custom…</option>
            </select>
            <input
              type="number"
              min={5}
              max={180}
              value={interval}
              onChange={(e) => setIntervalMin(parseInt(e.target.value, 10) || 0)}
              className="comic-input"
              style={{ padding: "0.45rem 0.6rem", width: "5rem" }}
              data-testid="schedule-interval-input"
            />
            <button
              onClick={saveInterval}
              disabled={savingInterval}
              className="comic-btn comic-btn-primary"
              data-testid="schedule-interval-save"
              style={{ padding: "0.5rem 0.9rem", fontSize: "0.95rem" }}
            >
              {savingInterval ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>
      </div>

      <div className="comic-card-static p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-['Bebas_Neue'] text-2xl tracking-wide">
              Weekly Hours
            </div>
            <p className="text-sm text-[#0B132B]/70 mt-1">
              Default booking windows by day of week.
            </p>
          </div>
          <button
            onClick={saveHours}
            disabled={savingHours}
            className="comic-btn comic-btn-primary"
            data-testid="schedule-save-hours"
            style={{ padding: "0.55rem 1rem", fontSize: "0.95rem" }}
          >
            {savingHours ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {DOW.map((d) => (
            <div
              key={d.key}
              className="flex items-center gap-3 border-2 border-[#0B132B]/15 rounded-lg p-2"
            >
              <div className="font-semibold w-24 shrink-0">{d.label}</div>
              <input
                type="time"
                value={hours[d.key].start}
                onChange={(e) => updateHour(d.key, "start", e.target.value)}
                className="comic-input"
                style={{ padding: "0.4rem 0.5rem", boxShadow: "2px 2px 0 #0B132B" }}
                data-testid={`schedule-${d.key}-start`}
              />
              <span className="text-[#0B132B]/60 text-sm">to</span>
              <input
                type="time"
                value={hours[d.key].end}
                onChange={(e) => updateHour(d.key, "end", e.target.value)}
                className="comic-input"
                style={{ padding: "0.4rem 0.5rem", boxShadow: "2px 2px 0 #0B132B" }}
                data-testid={`schedule-${d.key}-end`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="comic-card-static p-5 sm:p-6">
        <div className="font-['Bebas_Neue'] text-2xl tracking-wide">
          Day-Specific Overrides
        </div>
        <p className="text-sm text-[#0B132B]/70 mt-1">
          Block out a date (vacation, holiday) or set custom hours for one day.
        </p>

        <form onSubmit={addOverride} className="mt-4 grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-3">
            <label className="block text-xs font-semibold mb-1">Date</label>
            <input
              type="date"
              value={ovrDate}
              onChange={(e) => setOvrDate(e.target.value)}
              className="comic-input"
              style={{ padding: "0.5rem 0.6rem" }}
              required
              data-testid="schedule-ovr-date"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs font-semibold mb-1">Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOvrBlackout(true)}
                className="slot flex-1"
                data-selected={ovrBlackout ? "true" : "false"}
                style={{ padding: "0.55rem 0.5rem", fontSize: "0.9rem" }}
                data-testid="schedule-ovr-blackout"
              >
                Blackout
              </button>
              <button
                type="button"
                onClick={() => setOvrBlackout(false)}
                className="slot flex-1"
                data-selected={!ovrBlackout ? "true" : "false"}
                style={{ padding: "0.55rem 0.5rem", fontSize: "0.9rem" }}
                data-testid="schedule-ovr-custom"
              >
                Custom hours
              </button>
            </div>
          </div>
          {!ovrBlackout && (
            <>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold mb-1">Start</label>
                <input
                  type="time"
                  value={ovrStart}
                  onChange={(e) => setOvrStart(e.target.value)}
                  className="comic-input"
                  style={{ padding: "0.5rem 0.6rem" }}
                  required
                  data-testid="schedule-ovr-start"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold mb-1">End</label>
                <input
                  type="time"
                  value={ovrEnd}
                  onChange={(e) => setOvrEnd(e.target.value)}
                  className="comic-input"
                  style={{ padding: "0.5rem 0.6rem" }}
                  required
                  data-testid="schedule-ovr-end"
                />
              </div>
            </>
          )}
          {ovrBlackout && (
            <div className="sm:col-span-4">
              <label className="block text-xs font-semibold mb-1">Note (optional)</label>
              <input
                type="text"
                value={ovrNote}
                onChange={(e) => setOvrNote(e.target.value)}
                placeholder="Vacation, holiday, etc."
                className="comic-input"
                style={{ padding: "0.5rem 0.6rem" }}
                data-testid="schedule-ovr-note"
              />
            </div>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={savingOvr}
              className="comic-btn comic-btn-action w-full"
              style={{ padding: "0.55rem 0.5rem", fontSize: "0.95rem" }}
              data-testid="schedule-ovr-submit"
            >
              {savingOvr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
        </form>

        <div className="mt-5">
          <div className="font-['Bebas_Neue'] text-lg tracking-wide mb-2">
            Current Overrides
          </div>
          {overrides.length === 0 && (
            <div className="text-sm text-[#0B132B]/70">None set.</div>
          )}
          <div className="space-y-2">
            {overrides.map((o) => (
              <div
                key={o.date}
                data-testid={`schedule-ovr-row-${o.date}`}
                className="flex items-center justify-between gap-3 border-2 border-[#0B132B]/15 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-[#1E88E5]" />
                  <div>
                    <div className="font-semibold">{formatDateLabel(o.date)}</div>
                    <div className="text-xs text-[#0B132B]/70">
                      {o.blackout ? (
                        <span className="text-[#FF3D00] font-semibold">CLOSED</span>
                      ) : (
                        <>
                          {o.start} – {o.end}
                        </>
                      )}
                      {o.note ? ` · ${o.note}` : ""}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => deleteOverride(o.date)}
                  className="text-[#FF3D00] inline-flex items-center gap-1 text-sm font-semibold underline"
                  data-testid={`schedule-ovr-delete-${o.date}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
